import { CONTRACTOR_BADGE_VERIFICATION_STATUS } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback } from "react";

import { useCreateContractorBadgeVerification } from "../api/useCreateContractorBadgeVerification";
import { invalidateContractorBadgeVerification } from "../api/useGetContractorBadgeVerification";
import {
  BadgeVerificationWindowDays,
  getWorkerBadgeVerificationsQueryKey,
} from "../api/useGetWorkerBadgeVerifications";
import { useUpdateContractorBadgeVerification } from "../api/useUpdateContractorBadgeVerification";
import { useUploadBadgeVerificationPhoto } from "../api/useUploadBadgeVerificationPhoto";
import { BADGE_VERIFICATION_FLOW } from "../constants";
import { type BadgeVerificationPhoto } from "../types";

interface UseBadgeVerificationSubmitProps {
  shiftId: string;
  workerId: string;
  photo: BadgeVerificationPhoto | undefined;
  onSubmitPhoto: () => void;
  onSkipSuccess: () => void;
}

export function useBadgeVerificationSubmit(props: UseBadgeVerificationSubmitProps) {
  const { shiftId, workerId, photo, onSubmitPhoto, onSkipSuccess } = props;

  const queryClient = useQueryClient();
  const { showErrorToast, showInfoToast } = useToast();

  const { mutateAsync: createVerification, isLoading: isCreatingVerification } =
    useCreateContractorBadgeVerification();
  const { mutateAsync: updateVerification, isLoading: isUpdatingVerification } =
    useUpdateContractorBadgeVerification();
  const { mutateAsync: uploadToS3, isLoading: isUploadingToS3 } = useUploadBadgeVerificationPhoto();

  const isSubmitting = isCreatingVerification || isUploadingToS3 || isUpdatingVerification;

  // Verification already completed server-side (e.g., user navigated away mid-submission).
  const handleAlreadyCompleted = useCallback(async () => {
    logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_ALREADY_COMPLETED, { shiftId });
    await invalidateContractorBadgeVerification(queryClient, shiftId);
    showInfoToast("Badge verification was already completed.");
  }, [queryClient, shiftId, showInfoToast]);

  const handleSubmit = useCallback(async () => {
    if (!isDefined(photo)) {
      return;
    }

    try {
      // Step 1: Create verification record and get presigned S3 upload URL
      const { data: verification } = await createVerification({ shiftId });
      const { uploadUrl } = verification.attributes;
      const verificationId = verification.id;

      // Step 2: Upload photo to S3 using presigned URL
      await uploadToS3({ uploadUrl, photoBlob: photo.blob });

      // Step 3: Confirm the upload by updating verification status
      await updateVerification({
        verificationId,
        status: CONTRACTOR_BADGE_VERIFICATION_STATUS.UPLOADED,
        shiftId,
      });

      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_PHOTO_SUBMITTED, { shiftId });
      onSubmitPhoto();
    } catch (error) {
      // A 422 means the verification was already completed server-side (e.g., the user
      // navigated away mid-submission but the request succeeded). Invalidate the cache
      // so the UI reflects the completed state, then proceed with clock-in.
      if (isAxiosError(error) && error.response?.status === 422) {
        await handleAlreadyCompleted();
        logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_PHOTO_SUBMITTED, { shiftId });
        onSubmitPhoto();
        return;
      }

      logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_SUBMIT_FAILURE, {
        error,
        metadata: { shiftId, flow: BADGE_VERIFICATION_FLOW.UPLOAD },
      });
      showErrorToast("Failed to submit badge photo. Please try again.");
    }
  }, [
    photo,
    shiftId,
    createVerification,
    uploadToS3,
    updateVerification,
    onSubmitPhoto,
    showErrorToast,
    handleAlreadyCompleted,
  ]);

  const handleSkipToClockIn = useCallback(async () => {
    try {
      // Step 1: Create verification record (idempotent - will return existing if exists)
      const createResponse = await createVerification({ shiftId });
      const verificationId = createResponse.data.id;

      // Step 2: Mark verification as skipped
      await updateVerification({
        verificationId,
        status: CONTRACTOR_BADGE_VERIFICATION_STATUS.SKIPPED,
        shiftId,
      });

      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_SKIPPED, { shiftId });
      await queryClient.invalidateQueries(
        getWorkerBadgeVerificationsQueryKey(
          workerId,
          BadgeVerificationWindowDays.MISSED_BADGES,
          true
        )
      );
      onSkipSuccess();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        await handleAlreadyCompleted();
        logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_SKIPPED, { shiftId });
        await queryClient.invalidateQueries(
          getWorkerBadgeVerificationsQueryKey(
            workerId,
            BadgeVerificationWindowDays.MISSED_BADGES,
            true
          )
        );
        onSkipSuccess();
        return;
      }

      logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_SUBMIT_FAILURE, {
        error,
        metadata: { shiftId, flow: BADGE_VERIFICATION_FLOW.SKIP },
      });
      showErrorToast("Failed to skip badge verification. Please try again.");
    }
  }, [
    shiftId,
    workerId,
    createVerification,
    updateVerification,
    queryClient,
    showErrorToast,
    handleAlreadyCompleted,
    onSkipSuccess,
  ]);

  return { handleSubmit, handleSkipToClockIn, isSubmitting };
}
